import { isDefined } from '@sgme/fp';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import type { Sales } from 'state/secondaryMarket/shared/referential/referential.model';
import { getAvailableSalesWithSelecteDesk } from 'state/secondaryMarket/utils';
import type { AppState } from 'state/state.model';
import type { LoadingStatus } from 'utils/status';

import type { ClientState, CpOrderSettlementType } from './client.model';

function selectClientState(state: AppState, tabId: string): ClientState | undefined {
  return state.secondaryMarket.cpOrder.referential.client[tabId];
}

export function getAllSales(state: AppState, tabId: string): Sales[] | undefined {
  return selectClientState(state, tabId)?.data?.allSales;
}

export function getSelectedSaleIdFromDesk(state: AppState, tabId: string, desk?: string): string | undefined {
  const allSales = getAllSales(state, tabId);
  const salesByDesk = getAvailableSalesWithSelecteDesk(allSales, desk);
  const defaultSelectedSale = selectClientState(state, tabId)?.data?.selectedSale;
  const selectedSale = salesByDesk?.find(sale => sale.id === defaultSelectedSale);
  if (!selectedSale) {
    return salesByDesk.length === 1 ? salesByDesk[0].id : undefined;
  }
  return selectedSale.id;
}

export function getSaleFromId(state: AppState, tabId: string, salesId: string) {
  const allSales = getAllSales(state, tabId);
  if (allSales === undefined) {
    return undefined;
  }
  return allSales.find(sales => sales.id === salesId);
}

export function getSalesCountryFromId(state: AppState, tabId: string, salesId: string | undefined) {
  if (salesId === undefined) {
    return undefined;
  }
  const sale = getSaleFromId(state, tabId, salesId);
  return sale?.country;
}

export function getSalesFirstnameFromId(state: AppState, tabId: string, salesId: string) {
  const sale = getSaleFromId(state, tabId, salesId);
  return sale?.firstName;
}

export function getClientLoadingStatus(appState: AppState, tabId: string): LoadingStatus {
  const state = selectClientState(appState, tabId);
  if (state?.isLoading) {
    return 'loading';
  }
  if (isDefined(state?.error)) {
    return 'error';
  }
  return state?.data !== undefined ? 'success' : 'idle';
}

export function isClientLoading(appState: AppState, tabId: string): boolean {
  const state = selectClientState(appState, tabId);
  return state?.isLoading ?? true;
}

export function getClientSettlementTypes(appState: AppState, tabId: string) {
  const state = selectClientState(appState, tabId);
  return state?.data?.settlements;
}

export function getClientError(appState: AppState, tabId: string) {
  const state = selectClientState(appState, tabId);
  return state?.error;
}
export function getClientDefaultSettlementType(
  appState: AppState,
  tabId: string,
  way: TradeWay | undefined,
): CpOrderSettlementType | undefined {
  const settlementsTypes = getClientSettlementTypes(appState, tabId);
  if (way === 'ClientBuy') {
    return settlementsTypes?.find(x => x.isDefaultBuy);
  }
  if (way === 'ClientSell') {
    return settlementsTypes?.find(x => x.isDefaultSell);
  }
}

export function getAstroClient(appState: AppState, tabId: string): ISalesCounterpart | undefined {
  const state = selectClientState(appState, tabId);
  return state?.data?.client;
}
